import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import type { InitConfig } from '../types';

type DatadogInitConfig = NonNullable<InitConfig['datadog']>;

/**
 * Nested mode must not touch the bundled `datadogRum`: importing it reassigns
 * `window.DD_RUM` to a second, never-initialised instance whose calls buffer forever.
 * The host pins the instance it initialised on `__gcHostRum` (written by
 * `apps/registration-app/server/views/observability.js` — keep the property name in sync);
 * `DD_RUM` is the fallback for a host that has not been redeployed yet. `onReady` covers
 * the window where the agent is still downloading.
 */
const resolveHostRum = () => (window as any).__gcHostRum || (window as any).DD_RUM;

const callHostRum = (method: string, ...args: any[]): void => {
  const rum = resolveHostRum();
  if (!rum) {
    return;
  }
  const invoke = () => {
    // Re-resolve: the stub that queued this callback is not the instance that ran init.
    const ready = resolveHostRum();
    if (ready && typeof ready[method] === 'function') {
      ready[method](...args);
    }
  };
  if (typeof rum.onReady === 'function') {
    rum.onReady(invoke);
    return;
  }
  invoke();
};

export default {
  isInitialized: false,
  nestedSurface: '' as string,
  init(config: Omit<DatadogInitConfig, 'enabled'>): void {
    if (config.mode === 'nested') {
      this.nestedSurface = config.service;
      if (config.version) {
        callHostRum('setGlobalContextProperty', 'builderVersion', config.version);
      }
      this.isInitialized = true;
      return;
    }

    if (!config.applicationId || !config.clientToken) {
      return;
    }

    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      service: config.service,
      env: config.env,
      version: config.version,
      site: 'datadoghq.com',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      enablePrivacyForActionName: true,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackSessionAcrossSubdomains: config.trackSessionAcrossSubdomains,
      useSecureSessionCookie: true,
      useCrossSiteSessionCookie: config.useCrossSiteSessionCookie,
      traceSampleRate: 25,
      traceContextInjection: 'sampled',
      allowedTracingUrls: [window.location.origin],
      trackingConsent: config.trackingConsent ?? 'granted',
      silentMultipleInit: true
    });

    datadogLogs.init({
      clientToken: config.clientToken,
      service: config.service,
      env: config.env,
      version: config.version,
      site: 'datadoghq.com',
      sessionSampleRate: 100,
      trackSessionAcrossSubdomains: config.trackSessionAcrossSubdomains,
      useSecureSessionCookie: true,
      useCrossSiteSessionCookie: config.useCrossSiteSessionCookie,
      forwardErrorsToLogs: false,
      trackingConsent: config.trackingConsent ?? 'granted',
      silentMultipleInit: true
    });

    datadogRum.setGlobalContextProperty('surface', config.service);

    this.isInitialized = true;
  },
  identify(opaqueId: string): void {
    if (!this.isInitialized || !opaqueId) {
      return;
    }
    if (this.nestedSurface) {
      callHostRum('setUser', { id: opaqueId });
      return;
    }
    datadogRum.setUser({ id: opaqueId });
  },
  // Withdraw or grant RUM/log collection at runtime; Datadog stops/starts natively.
  setTrackingConsent(consent: 'granted' | 'not-granted'): void {
    if (!this.isInitialized) {
      return;
    }
    if (this.nestedSurface) {
      callHostRum('setTrackingConsent', consent);
      return;
    }
    datadogRum.setTrackingConsent(consent);
    datadogLogs.setTrackingConsent(consent);
  },
  error(error: unknown, errorContext?: object): void {
    if (!this.isInitialized) {
      return;
    }

    const merged = {
      ...(this.nestedSurface ? { surface: this.nestedSurface } : {}),
      ...(errorContext ?? {})
    };
    const context = Object.keys(merged).length > 0 ? merged : undefined;
    const addError = (reported: Error) =>
      this.nestedSurface ? callHostRum('addError', reported, context) : datadogRum.addError(reported, context);

    if (error instanceof Error) {
      addError(error);
      return;
    }

    if (typeof error === 'string') {
      addError(new Error(error));
      return;
    }

    try {
      addError(new Error(JSON.stringify(error)));
    } catch {
      addError(new Error(String(error ?? '')));
    }
  },
  info(message: string, context?: object): void {
    // Console-only when nested: the host loads the RUM agent but no Logs instance.
    if (!this.isInitialized || this.nestedSurface) {
      return;
    }
    datadogLogs.logger.info(message, context);
  },
  addFeatureFlag(flagName: string, variant: string): void {
    if (!this.isInitialized) {
      return;
    }
    if (this.nestedSurface) {
      callHostRum('addFeatureFlagEvaluation', flagName, variant);
      return;
    }
    datadogRum.addFeatureFlagEvaluation(flagName, variant);
  },
  addMetadata(key: string, value: any): void {
    if (!this.isInitialized) {
      return;
    }
    if (this.nestedSurface) {
      // A nested surface writes into the host's global context, where the key would
      // otherwise land on the host page's own events too. Namespace it by surface so it
      // stays attributable: goldcast-builder's `hub_id` becomes `builder_hub_id`.
      callHostRum('setGlobalContextProperty', `${this.nestedSurface.replace(/^goldcast-/, '')}_${key}`, value);
      return;
    }
    datadogRum.setGlobalContextProperty(key, value);
  }
};
